import { useTranslations } from "next-intl";
import type { DietCatalogCateringType } from "@/features/diets/types";
import { getLocalePrice } from "@/lib/utilities";
import { cn } from "@/lib/utils";

const EqualCost = ({
  price,
  t,
}: {
  price: DietCatalogCateringType["deliveryCosts"]["price"];
  t: ReturnType<typeof useTranslations>;
}) => {
  return t("equal", {
    price: getLocalePrice({
      price: price ?? 0,
      options: { trailingZeroDisplay: "stripIfInteger" },
    }),
  });
};

const FromCost = ({
  price,
  t,
}: {
  price: DietCatalogCateringType["deliveryCosts"]["price"];
  t: ReturnType<typeof useTranslations>;
}) => {
  return t("from", {
    price: getLocalePrice({
      price: price ?? 0,
      options: { trailingZeroDisplay: "stripIfInteger" },
    }),
  });
};

const FreeCost = ({ t }: { t: ReturnType<typeof useTranslations> }) => {
  return t("free");
};

const FromWithoutPriceCost = ({
  t,
}: {
  t: ReturnType<typeof useTranslations>;
}) => {
  return t("fromNoPrice");
};

const DeliveryCostComponent = ({
  type,
  price,
  t,
}: {
  type: DietCatalogCateringType["deliveryCosts"]["type"];
  price: DietCatalogCateringType["deliveryCosts"]["price"];
  t: ReturnType<typeof useTranslations>;
}) => {
  switch (type) {
    case "equal":
      return <EqualCost price={price} t={t} />;
    case "from":
      return <FromCost price={price} t={t} />;
    case "free":
      return <FreeCost t={t} />;
    case "none":
      return null;
    case "multiple":
      return <FromWithoutPriceCost t={t} />;
    default:
      return null;
  }
};

const Wrapper = ({
  children,
  align,
  className,
}: {
  children: React.ReactNode;
  align: "left" | "right";
  className: string;
}) => (
  <div
    className={cn(
      "my-1 mb-2 w-full text-brand-green1-300 leading-5",
      className,
    )}
    style={{
      textAlign: align,
    }}
  >
    {children}
  </div>
);

export const DeliveryCost = ({
  type,
  price,
  align = "right",
  hideNotFree = false,
  calculated = false,
  className = "",
  textType = "full",
}: DietCatalogCateringType["deliveryCosts"] & {
  align?: "left" | "right";
  hideNotFree?: boolean;
  calculated?: boolean;
  className?: string;
  textType?: "full" | "slim";
}) => {
  const t = useTranslations(
    textType === "full" ? "deliveryCosts" : "deliveryCostsSlim",
  );

  if (calculated && hideNotFree && type !== "free") {
    return null;
  }

  return DeliveryCostComponent ? (
    <Wrapper align={align} className={className}>
      <DeliveryCostComponent price={price} t={t} type={type} />
    </Wrapper>
  ) : null;
};
